import type { Market, PredictionWithMarket } from '@/common/predictionMarkets/types';

export enum EVENTS {
  PREDICTION_UPDATE = 'prediction-update',
  MARKET_SETTLED = 'market-settled',
  MARKET_CANCELLED = 'market-cancelled',
  PREDICTION_PLACED = 'prediction-placed',
  MARKET_CREATED = 'market-created',
  MARKET_DATA_STALE = 'market-data-stale',
  MARKET_DATA_UPDATED = 'market-data-updated',
  PLAY_BALANCE_TOP_UP = 'play-balance-top-up',
  CREATOR_UPDATED = 'creator-updated',
  CREATOR_REGISTERED = 'creator-registered',
}

export type EventMap = {
  [EVENTS.PREDICTION_UPDATE]: {
    id: string; // composite key: `${contractAddress}-${userId}-${marketId}-${predictionId}`
    contractAddress: string;
    userId: string;
    marketId: string;
    predictionWithMarket: PredictionWithMarket;
    isComplete: number;
    updatedAtMs: number;
  };
  [EVENTS.MARKET_SETTLED]: {
    creatorId: string; // numeric creator ID
    creatorAddress: string;
    market: Market;
    winningOutcomeId: string;
    winningOutcomeName: string;
    creatorFeesEarned: number;
  };
  [EVENTS.MARKET_CANCELLED]: {
    creatorId: string; // numeric creator ID
    creatorAddress: string;
    market: Market;
  };
  [EVENTS.PREDICTION_PLACED]: {
    userId: string;
    marketId: string;
    marketTitle: string;
    outcomeId: string;
    outcomeName: string;
    amount: bigint;
    creatorId: string; // numeric creator ID
  };
  [EVENTS.MARKET_CREATED]: {
    creatorId: string; // numeric creator ID
    creatorAddress: string;
    market: Market;
  };
  [EVENTS.MARKET_DATA_STALE]: {
    marketId: string;
    creatorId: string; // numeric creator ID - allows filtering by creator
  };
  [EVENTS.MARKET_DATA_UPDATED]: {
    marketId: string;
    creatorId: string; // numeric creator ID
    market?: Market; // Optional: pass pre-fetched market to skip redundant fetch
  };
  [EVENTS.PLAY_BALANCE_TOP_UP]: {
    amount: bigint;
  };
  [EVENTS.CREATOR_UPDATED]: {
    creatorId: string; // numeric creator ID
    creatorAddress: string;
  };
  [EVENTS.CREATOR_REGISTERED]: {
    creatorId: string; // numeric creator ID
    creatorAddress: string;
  };
};

export function useEvents() {
  function emit<K extends keyof EventMap>(event: K, payload: EventMap[K]) {
    window.dispatchEvent(new CustomEvent<EventMap[K]>(event, { detail: payload }));
  }

  function on<K extends keyof EventMap>(event: K, callback: (payload: EventMap[K]) => void) {
    const handler = (e: Event) => {
      const customEvent = e as CustomEvent<EventMap[K]>;
      callback(customEvent.detail);
    };

    window.addEventListener(event, handler);

    return () => {
      window.removeEventListener(event, handler);
    };
  }

  return { emit, on };
}
